import { v4 as uuidv4 } from 'uuid';
import MenuSelector from './MenuSelector';
import InfoLink from './InfoLink';
import Checkbox from '@material-ui/core/Checkbox';
import { Typography } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import PropTypes from 'prop-types';
import React from 'react';
import GS from '../grafsync/grafsync';
import ActionText from './ActionText';
import DateSelector from './DateSelector';
import Errored from './Errored';
import { ProjectConsumer } from './providers/ProjectProvider';
import HistoryDownload from './HistoryDownload';
import Loading from './Loading';
import OptionsSelector from './OptionsSelector';
import Paging from './Paging';
import { globalStyles as styles } from './style/gstheme';
import SubscriptionUsers from './SubscriptionUsers';
import { isRTL, makeCancelable } from './utils/utils';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import { BN_NAMESPACE } from './utils/constants';

const PAGE_SIZE = 10;

const viewOptions = [
    {
        value: "label",
        label: "Label/Title"
    },
    {
        value: "resource",
        label: "Resource"
    },
    {
        value: "revision",
        label: "Rev."
    },
    {
        value: "date",
        label: "Date"
    },
    {
        value: "user",
        label: "User"
    },
    {
        value: "type",
        label: "Type"
    },
    {
        value: "propclass",
        label: "Property/Class"
    },
    {
        value: "original",
        label: "Original"
    },
    {
        value: "new",
        label: "New"
    },
    {
        value: "language",
        label: "Language"
    },
    {
        value: "datatype",
        label: "Datatype"
    }
];

const editColours = {
    Addition: 'rgb(0, 169, 224, 0.2)',
    Deletion: 'rgb(221, 44, 0, 0.3)',
    Deleted: 'rgb(221, 44, 0, 0.5)',
    Update: 'rgb(2, 128, 144, 0.2)',
    Created: 'rgb(151, 215, 0, 0.3)',
    "List update": 'rgb(0, 0, 255, 0.2)',
    "IRI update": 'rgb(252, 186, 3, 0.3)',
    "IRI change setup": 'rgb(252, 186, 3, 0.3)'
};

class HistoryViewer extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            page: 1,
            history: undefined,
            loadID: uuidv4(),
            startDate: props.forceStartDate ? props.startTimestamp : undefined,
            useStartDate: props.forceStartDate,
            endDate: undefined,
            users: null,
            pageCount: 1,
            pageSize: PAGE_SIZE,
            loading: false,
            columns: [true, false, true, true, true, true, true, true, true, false, false],
            viewType: "option-0",
        };
        this.cancellable1 = null;
    }

    getHistory = (page) => {
        const offset = (page - 1) * this.state.pageSize;
        const option = parseInt(this.state.viewType.substring(7));
        const path = this.props.options && this.props.options[option] && this.props.options[option].path ?
            this.props.options[option].path
            :
            "";
        const isReversed = this.props.options && this.props.options[option] && this.props.options[option].isReversed;
        const { startDate, endDate, user } = this.state;
        const users = user ? [user] : null;
        this.cancellable1 = makeCancelable(GS.history(this.props.id, this.props.graph, path, isReversed, startDate, endDate, users, this.state.pageSize + 1, offset));
        return this.cancellable1;
    };

    componentDidMount() {
        this.loadHistory(1, this.state.loadID);
    }

    componentWillUnmount() {
        if (this.cancellable1) this.cancellable1.cancel();
    }

    handleUseStartDateChange = (event) => {
        const isChecked = event.target.checked;
        const update = {
            useStartDate: isChecked,
            page: 1,
            pageCount: 1
        };
        if (isChecked) {
            update.startDate = new Date(this.props.startTimestamp);
            update.endDate = new Date().toJSON().slice(0, 10);
        }
        else {
            update.startDate = undefined;
            update.endDate = undefined;
        }
        this.setState(update, () => this.loadHistory());
    };

    handleDateRangeChange = (startDate, endDate) => {
        this.setState({
            page: 1,
            startDate,
            pageCount: 1,
            useStartDate: false,
            endDate
        }, () => this.loadHistory());
    };

    loadData(iris) {
        this.cancellable1 = makeCancelable(GS.loadData(iris, {}, this.props.graph, true));
        return this.cancellable1;
    }

    loadHistory = (page = 1) => {
        const loadID = uuidv4();
        this.setState({ loading: true, loadID }, async () => {
            try {
                const history = await this.getHistory(page).promise;
                if (this.cancellable1 && this.cancellable1.isCancelled()) return;
                // Might have been a later load so abandon this one
                if (loadID !== this.state.loadID) return;
                const viewingOption = parseInt(this.state.viewType.substring(7));
                const showResource = this.props.options && this.props.options.length > 0 ?
                    this.props.options[viewingOption].resource === true
                    :
                    false;
                // If resources are showing then load all in one go
                if (showResource) {
                    const iriList = history.map(h => h.resourceOfChange);
                    await this.loadData([...new Set(iriList)]).promise;
                    if (this.cancellable1 && this.cancellable1.isCancelled()) return;
                }
                const isAnotherPage = history.length > this.state.pageSize;
                const pageCount = isAnotherPage && page === this.state.pageCount ?
                    this.state.pageCount + 1
                    :
                    this.state.pageCount;
                this.setState({ history, page, pageCount, loading: false });
            }
            catch (e) {
                if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ isErrored: e });
            }
        });
    };

    handleUserChange = (user) => this.setState({
        page: 1,
        pageCount: 1,
        user: user === "All" ? null : user
    }, () => this.loadHistory());

    onChange = (event) => {
        this.setState({
            viewType: event.target.value,
            history: undefined,
            columns: [true, false, true, true, true, true, true, true, true, false, false],
            page: 1,
            pageCount: 1
        }, () => this.loadHistory());
    };

    handlePageChange = (page) => this.loadHistory(page);

    onPageSizeChange = (pageSize) => this.setState({
        page: 1,
        pageCount: 1,
        pageSize
    }, () => this.loadHistory());

    setColumns = (columns) => this.setState({ columns });

    render() {

        const { classes, displayTitle, options, id, graph, onToggleView, displayFilters, compactNoEdits,
            otherComponent, projectGraph, title, suppressLoading } = this.props;
        const { startDate, endDate, user, history, page, pageCount, isErrored, viewType, columns, loading, useStartDate, pageSize } = this.state;
        const viewingOption = parseInt(viewType.substring(7));
        const path = this.props.options && this.props.options[viewingOption] && this.props.options[viewingOption].path ?
            this.props.options[viewingOption].path
            :
            "";
        const isReversed = this.props.options && this.props.options[viewingOption] && this.props.options[viewingOption].isReversed;
        const users = user ? [user] : null;
        const showResource = this.props.options && this.props.options.length > 0 ?
            this.props.options[viewingOption].resource === true
            :
            false;

        if (isErrored) return <Errored data={isErrored} />;

        return (
            <ProjectConsumer>
                {({ id: project, defaultLang, hasComponent, type: projectType }) => (
                    <div style={!displayTitle ? { margin: '6px' } : { marginBottom: 24 }}>
                        {displayTitle && <Typography
                            paragraph
                            className={classes.mainHeading}
                            style={{ marginTop: 10, ...(!displayFilters && displayTitle) && { marginBottom: 0 } }}>{title}</Typography>}
                        {displayFilters &&
                            <div data-test="history-filtering" className={classes.filterArea}>
                                <Grid container spacing={2} classes={
                                    {
                                        "spacing-xs-2": this.props.classes.fullGrid
                                    }}>
                                    <Grid item xs={6}>
                                        <DateSelector
                                            key={useStartDate}
                                            startDate={startDate}
                                            endDate={endDate}
                                            dataTest="date-selector"
                                            label="Date Range"
                                            selected="last7"
                                            onSetDate={this.handleDateRangeChange} />
                                        {this.props.startTimestamp &&
                                            <div>
                                                <FormControlLabel
                                                    data-test="option-label"
                                                    control={
                                                        <Checkbox
                                                            data-test={"option-date"}
                                                            checked={useStartDate}
                                                            onChange={this.handleUseStartDateChange}
                                                            name="useStartDate"
                                                            classes={{ root: classes.compactCheckbox }}
                                                        />
                                                    }
                                                    label={this.props.startDateLabel + " (From " + new Date(this.props.startTimestamp).toLocaleString() + ")"}
                                                    classes={{ label: classes.formLabelInlineLight }}
                                                />
                                            </div>
                                        }
                                    </Grid>
                                    <Grid item xs={6}>
                                        <SubscriptionUsers onUserChange={this.handleUserChange} />
                                    </Grid>
                                </Grid>
                                <div style={{
                                    marginLeft: 12,
                                    marginTop: 12,
                                    marginRight: 12,
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                }}>
                                    <div>
                                        {options &&
                                            <FormControl component="fieldset">
                                                <legend id="history-scope" className={classes.subHeading}>History Scope</legend>
                                                <RadioGroup
                                                    row
                                                    name="viewType"
                                                    value={viewType}                                                    
                                                    onChange={this.onChange}>
                                                    {options.map((option, index) =>
                                                        <FormControlLabel
                                                            data-test={"option-" + index}
                                                            key={index} value={"option-" + index}
                                                            control={<Radio color="primary" />}
                                                            label={option.label}
                                                            classes={{ label: classes.formLabelInlineLight }}
                                                        />
                                                    )}
                                                </RadioGroup>
                                            </FormControl>
                                        }
                                    </div>
                                    <div style={{ marginTop: 12, marginBottom: options ? 0 : 12 }}>
                                        <HistoryDownload
                                            disabled={!history || history.length === 0}
                                            resource={id}
                                            graph={graph}
                                            startDate={startDate}
                                            endDate={endDate}
                                            users={users}
                                            isReversed={isReversed}
                                            path={path} />
                                    </div>
                                </div>
                            </div>
                        }
                        {history === undefined || loading ?
                            suppressLoading ?
                                <div data-test="history-viewer" style={{ marginTop: 40, paddingBottom: compactNoEdits ? 20 : 120 }}>
                                </div>
                                :
                                <div style={{height: 240 }}>
                                    <Loading offset="140px" message="Working on getting the history ..." />
                                </div>
                            :
                            history.length > 0 ?
                                <div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        justifyContent: 'flex-end',
                                        marginTop: displayFilters ?
                                            12
                                            :
                                            displayTitle ?
                                                -32
                                                :
                                                0,
                                        marginRight: 12
                                    }}>
                                        <div style={{ marginRight: 24, transform: 'translateY(7px)' }}>
                                            <OptionsSelector
                                                options={viewOptions}
                                                visibleValues={[showResource, showResource, true, true, true, true, true, true, true, true, true]}
                                                values={columns}
                                                onSelect={this.setColumns} />
                                        </div>
                                        <div style={{ width: 120 }}>
                                            <MenuSelector
                                                dataTest="page-size-selector"
                                                label="Edits to Show"
                                                handleChange={this.onPageSizeChange}
                                                selected={pageSize.toString()}
                                                hintWord="results size"
                                                options={[
                                                    {
                                                        value: 10,
                                                        label: "10"
                                                    },
                                                    {
                                                        value: 25,
                                                        label: "25"
                                                    },
                                                    {
                                                        value: 50,
                                                        label: "50"
                                                    }
                                                ]} />
                                        </div>
                                    </div>
                                    <div style={{ overflowX: 'scroll' }}>
                                        <Table data-test="history-viewer" style={{ maxWidth: '100%' }}>
                                            <TableHead>
                                                <TableRow data-test="headers">
                                                    {
                                                        showResource && columns[0] &&
                                                        <TableCell data-test="label-title" component="th" className={classes.tableHeading}>Label/Title</TableCell>
                                                    }
                                                    {
                                                        showResource && columns[1] &&
                                                        <TableCell component="th" className={classes.tableHeading}>Resource</TableCell>
                                                    }
                                                    {
                                                        ["Rev.", "Date", "User", "Type", "Property/Class", "Original", "New", "Language", "Datatype"].map((col, index) =>
                                                            columns[index + 2] &&
                                                            <TableCell key={index} component="th" className={classes.tableHeading}>{col}</TableCell>
                                                        )
                                                    }
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {history.map((item, rowIndex) => {
                                                    if (rowIndex === this.state.pageSize) return null;
                                                    const date = item.timeOfChange ? new Date(item.timeOfChange) : null;
                                                    const dateText = date ? date.toLocaleString() : "";
                                                    const color = item.label["en"] in editColours ?
                                                        editColours[item.label["en"]]
                                                        :
                                                        'rgb(50, 91, 168, 0.2)';
                                                    const row = [
                                                        item.revisionNo,
                                                        dateText,
                                                        item.username || item.user,
                                                        <span style={{
                                                            display: 'block',
                                                            width: 'calc(100% - 8px)',
                                                            borderRadius: 4,
                                                            textAlign: 'center',
                                                            padding: '4px 2px',
                                                            backgroundColor: color,
                                                            color: 'black'
                                                        }}>{item.label["en"]}</span>,
                                                        item.propertyOfChange ?
                                                            <Tooltip title={item.propertyOfChange}>
                                                                <span style={{ display: 'block' }}>
                                                                    {GS.getAlias(item.propertyOfChange)}
                                                                </span>
                                                            </Tooltip>
                                                            :
                                                            item.resourceOfChangeType ?
                                                                item.resourceOfChangeType.map(classIRI => {
                                                                    const alias = GS.getAlias(classIRI);
                                                                    if (alias && alias !== "DatatypeConstraint") {
                                                                        return <Tooltip key={classIRI} title={classIRI}>
                                                                            <span style={{ display: 'block' }}>
                                                                                {alias}
                                                                            </span>
                                                                        </Tooltip>;
                                                                    }
                                                                    return null;
                                                                })
                                                                :
                                                                "",
                                                        item.originalIriAtPointInTime ? item.originalIriAtPointInTime : item.originalValue,
                                                        item.newIriAtPointInTime || item.newValue,
                                                        item.languageOfChange === "und" ? "No language" : item.languageOfChange,
                                                        item.datatypeOfChange
                                                    ];
                                                    const rtl = item.languageOfChange ? isRTL(item.languageOfChange) : false;
                                                    return (
                                                        <TableRow key={"row-" + rowIndex}
                                                            classes={{
                                                                root: classes.stripedRow
                                                            }}>
                                                            {
                                                                showResource && columns[0] &&
                                                                <TableCell className={classes.tableCell}
                                                                    style={{
                                                                        maxWidth: '300px',
                                                                        verticalAlign: 'middle'
                                                                    }}>
                                                                    <div style={{ marginTop: 6 }}>
                                                                        {item.resourceOfChange.startsWith(BN_NAMESPACE) ?
                                                                            <Typography className={classes.tableCellText}>
                                                                                <span className={classes.tableCellLine}>Blank node</span>
                                                                            </Typography>
                                                                            :
                                                                            item.resourceOfChangeType && item.resourceOfChangeType[0] === "https://graphifi.com/schema/xkos/Note" ?
                                                                                <Typography className={classes.tableCellText}>
                                                                                    <span className={classes.tableCellLine}>Note</span>
                                                                                </Typography>
                                                                                :
                                                                                <CommonIriDisplay
                                                                                    key={item.resourceOfChange}
                                                                                    id={item.resourceOfChange}
                                                                                    components={hasComponent}
                                                                                    projectIRI={project}
                                                                                    otherComponent={otherComponent}
                                                                                    handleNotAvailable={true}
                                                                                    projectType={projectType}
                                                                                    compact={true}
                                                                                    onLink={onToggleView}
                                                                                    graph={projectGraph || graph}
                                                                                    viewLang={defaultLang}
                                                                                    defaultLang={defaultLang} />
                                                                        }
                                                                    </div>
                                                                </TableCell>
                                                            }
                                                            {
                                                                showResource && columns[1] &&
                                                                <TableCell className={classes.tableCell}
                                                                    style={{
                                                                        maxWidth: '300px',
                                                                        verticalAlign: 'middle'
                                                                    }}>
                                                                    <Typography className={classes.tableCellText}>
                                                                        {
                                                                            item.resourceOfChange
                                                                        }
                                                                    </Typography>
                                                                </TableCell>
                                                            }
                                                            {row.map((cell, cellIndex) => {
                                                                if (!columns[cellIndex + 2]) return null;
                                                                const spans = cell && typeof cell === "string" ? cell.split("\n") : [cell];
                                                                return <TableCell
                                                                    key={cellIndex}
                                                                    className={classes.tableCell}
                                                                    style={
                                                                        cellIndex > 2 && cellIndex < 5 ?
                                                                            {
                                                                                minWidth: '100px',
                                                                                verticalAlign: 'middle',
                                                                            }
                                                                            :
                                                                            cellIndex === 1 || cellIndex === 2 ?
                                                                                {
                                                                                    whiteSpace: 'nowrap',
                                                                                    verticalAlign: 'middle'
                                                                                }
                                                                                :
                                                                                cellIndex === 5 ?
                                                                                    {
                                                                                        minWidth: '200px',
                                                                                        verticalAlign: 'middle'
                                                                                    }
                                                                                    :
                                                                                    {
                                                                                        verticalAlign: 'middle'
                                                                                    }}>
                                                                    <div style={{
                                                                        maxHeight: 200,
                                                                        width: '100%',
                                                                        overflowY: 'scroll',
                                                                        ...(rtl && (cellIndex === 5 || cellIndex === 6)) && {textAlign: 'right'}
                                                                    }}>
                                                                        {spans.map((spanText, spanIndex) => {
                                                                            return typeof spanText === "string" && spanText.startsWith(BN_NAMESPACE) ?
                                                                                <Typography className={classes.tableCellText} key={spanIndex}>
                                                                                    <span className={classes.tableCellLine}>Blank node</span>
                                                                                </Typography>
                                                                                :
                                                                                typeof spanText === "string" && spanText.startsWith("http") ?
                                                                                    <InfoLink
                                                                                        id={spanText}
                                                                                        key={spanIndex}
                                                                                        defaultLang={defaultLang}
                                                                                        graph={projectGraph || graph}
                                                                                        onToggleView={onToggleView}
                                                                                        components={hasComponent ? hasComponent.map(c => ({ id: c })) : []}
                                                                                        isReadOnly={true} />
                                                                                    :
                                                                                    <Typography
                                                                                        dir={rtl && (cellIndex === 5 || cellIndex === 6) ? "rtl" : null}
                                                                                        lang={item.languageOfChange ? item.languageOfChange : null}
                                                                                        className={classes.tableCellText}
                                                                                        key={spanIndex}>
                                                                                        <span className={classes.tableCellLine}>
                                                                                            {spanText === "" ? <br /> : spanText}
                                                                                        </span>
                                                                                    </Typography>;
                                                                        })}
                                                                    </div>
                                                                </TableCell>;
                                                            }
                                                            )}
                                                        </TableRow>
                                                    );
                                                }
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                                :
                                <div data-test="history-viewer" style={{ paddingBottom: compactNoEdits ? 20 : 120 }}>
                                    <ActionText format="result" offset={compactNoEdits ? "20px" : "120px"}>No edits have been made.</ActionText>
                                </div>
                        }
                        {history && history.length > 0 && pageCount > 1 && !loading &&
                            <div style={{ marginTop: '20px' }}>
                                <Paging page={page} pageCount={pageCount} pageItemsRange={2} handlePageChange={this.loadHistory} />
                            </div>
                        }
                    </div>
                )
                }
            </ProjectConsumer>
        );
    }
}

HistoryViewer.defaultProps = {
    displayTitle: true,
    displayFilters: true,
    otherComponent: false,
    compactNoEdits: false,
    title: "History",
    forceStartDate: false,
    suppressLoading: false
};

HistoryViewer.propTypes = {
    id: PropTypes.string.isRequired,
    otherComponent: PropTypes.bool,
    displayFilters: PropTypes.bool,
    suppressLoading: PropTypes.bool,
    forceStartDate: PropTypes.bool,
    title: PropTypes.string,
    options: PropTypes.array,
    compactNoEdits: PropTypes.bool,
    startTimestamp: PropTypes.string,
    onToggleView: PropTypes.func,
    startDateLabel: PropTypes.string,
    graph: PropTypes.string.isRequired,
    // For versions the graph we want to link to is not the same as the history graph
    projectGraph: PropTypes.string,
    displayTitle: PropTypes.bool,
    classes: PropTypes.object
};

export default withStyles(styles)(HistoryViewer);